import {
  Directive,
  ElementRef,
  HostListener,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';
import { Observable ,  Subscription } from 'rxjs';
import {fromEvent} from 'rxjs/internal/observable/fromEvent';


@Directive({
  selector: '[decimalsAutocomplete]',
  exportAs: 'decimals-autocomplete',
})
export class DecimalsAutocompleteDirective implements OnChanges, OnDestroy {

  @Input() control: UntypedFormControl;
  @Input() decimalsLimit: number = 4;
  @Input() digitsLimit: number;
  @Input() decimalsAutocompleteOn: boolean = true;

  @HostBinding('attr.placeholder')
  @Input() placeholder: string = '';

  private focusSubscr: Subscription;
  private blurSubscr: Subscription;
  private keypressSubscr: Subscription;
  private pasteSubscr: Subscription;

  get fullValue(): string {
    return _.get(this.control, 'value', '');
  }

  constructor(
    private elementRef: ElementRef,
    private ngZone: NgZone,
    private ref: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.decimalsAutocompleteOn) {
      if (changes.decimalsAutocompleteOn.currentValue) {
        this.subscribeEvents();
      } else {
        this.unsubscribeEvents();
      }
    }
    if (this.decimalsAutocompleteOn) {
      this.elementRef.nativeElement.value = this.cutValue(this.fullValue);
    }
  }

  ngOnDestroy() {
    this.unsubscribeEvents();
  }

  // private
  private subscribeFocus(): void {
    if (this.focusSubscr) { this.focusSubscr.unsubscribe(); }
    this.focusSubscr = fromEvent(this.elementRef.nativeElement, 'focus')
    .subscribe(($event: any) => {
      this.elementRef.nativeElement.value = this.fullValue;
      setTimeout(() => this.elementRef.nativeElement.setSelectionRange(0, 0), 0);
      this.ref.detectChanges();
    });
  }

  private subscribeBlur(): void {
    if (this.blurSubscr) { this.blurSubscr.unsubscribe(); }
    this.blurSubscr = fromEvent(this.elementRef.nativeElement, 'blur')
    .subscribe(($event: any) => {
      if (isNaN($event.target.value)) { return; }
      this.elementRef.nativeElement.value = this.cutValue($event.target.value);
      this.ref.detectChanges();
    });
  }

  private subscribeKeypress(): void {
    if (this.keypressSubscr) { this.keypressSubscr.unsubscribe(); }
    this.keypressSubscr = fromEvent(this.elementRef.nativeElement, 'keypress')
    .subscribe((event: any) => {
      const value: any = this.getNextValue(event);
      if (value === '.') { return; }
      if (isNaN(value) || this.isLimited(value)) {
        return event.preventDefault();
      }
      this.ref.detectChanges();
    });
  }

  private subscribePaste(): void {
    if (this.pasteSubscr) { this.pasteSubscr.unsubscribe(); }
    this.pasteSubscr = fromEvent(this.elementRef.nativeElement, 'paste')
    .subscribe((event: any) => {
      const clipboardData: any = event.clipboardData || (<any>window).clipboardData;
      if (!clipboardData) { return; }
      const pastedData: any = clipboardData.getData('Text');
      if (isNaN(pastedData) || this.isLimited(pastedData)) {
        return event.preventDefault();
      }
      this.ref.detectChanges();
    });
  }

  private subscribeEvents(): void {
    this.ngZone.runOutsideAngular(() => {
      this.subscribeFocus();
      this.subscribeBlur();
      this.subscribeKeypress();
      this.subscribePaste();
    });
  }

  private unsubscribeEvents(): void {
    if (this.focusSubscr) { this.focusSubscr.unsubscribe(); }
    if (this.blurSubscr) { this.blurSubscr.unsubscribe(); }
    if (this.keypressSubscr) { this.keypressSubscr.unsubscribe(); }
    if (this.pasteSubscr) { this.pasteSubscr.unsubscribe(); }
  }

  private cutValue(value: any): string {
    if (isNaN(value)) { return ''; }
    const split = String(value).split('.');
    const decimal = split[1];
    if (this.digitsLimit && this.getDigitsLength(value) >= this.digitsLimit) { return value; }
    if (decimal) {
      if (decimal.length < 2) { return `${split[0]}.${decimal}0`; }
      if (decimal.length > 2) { return `${split[0]}.${decimal.slice(0, 2)}`; }
      return value;
    } else {
      // if placeholder was defined then show placeholder for empty value;
      if (!value && this.placeholder) { return ''; }
      return `${!value ? '0' : value}.00`;
    }
  }

  private isLimited(value: any): boolean {
    if (this.digitsLimit) {
      return this.getDigitsLength(value) > this.digitsLimit;
    } else {
      const decimal = String(value).split('.')[1];
      return !decimal ? false : decimal.length > this.decimalsLimit;
    }
  }

  private getNextValue(event: any): string {
    const value = event.target.value;
    const start = event.target.selectionStart;
    const end = event.target.selectionEnd;
    return value.substring(0, start) + event.key + value.substring(end);
  }

  private getDigitsLength(value: string|number): number {
    return this.getDigits(value).length;
  }

  private getDigits(value: string|number): string {
    return String(value).replace(/\.|,/g, '');
  }
}
